<script setup lang="ts">
import { onUnmounted, ref } from 'vue'
import { default as PButton } from 'primevue/button'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useClipboard } from '@vueuse/core'

// Props
const props = defineProps<{
  text?: string
  label: string
  duration?: number // secondes (par défaut 10)
}>()

// Événements
const emit = defineEmits<{
  (e: 'copied'): void
  (e: 'copy-error', error: unknown): void
}>()

const visible = ref(false)
let hideTimer: number | null = null

function clearTimer() {
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
}

function startAutoHide() {
  clearTimer()
  const secs = props.duration ?? 10
  hideTimer = window.setTimeout(() => hide(), secs * 1000)
}

function reveal() {
  if (visible.value) return
  visible.value = true
  startAutoHide()
}

function hide() {
  if (!visible.value) return
  visible.value = false
  clearTimer()
}

const { copy, copied, isSupported } = useClipboard()

async function copyText() {
  if (!isSupported) {
    emit('copy-error')
    return
  }
  await copy(props.text)
  emit('copied')
  // relance le timer après copie
  startAutoHide()
}

// Nettoyage
onUnmounted(() => clearTimer())
</script>

<template>
  <div class="inline-flex w-full flex-col gap-2" @mouseleave="hide">
    <!-- Bouton révéler -->
    <p-button
      v-if="!visible"
      type="button"
      security="secondary"
      outlined
      class="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
      @click="reveal"
    >
      {{ label }}
    </p-button>

    <!-- Texte révélé -->
    <div v-else class="inline-flex max-w-full flex-col justify-start rounded-lg border p-3 text-sm">
      <div class="flex flex-row items-center justify-between">
        <!-- Contenu -->
        <p class="flex font-mono tracking-normal break-words whitespace-pre-wrap slashed-zero">
          {{ text }}
        </p>

        <!-- Boutons -->
        <div class="flex items-end gap-1">
          <p-button
            v-if="isSupported"
            type="button"
            outlined
            severity="info"
            class="rounded-md border px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/10"
            @click="copyText"
          >
            <font-awesome-icon icon="copy" />
          </p-button>
        </div>
      </div>

      <!-- Message feedback -->
      <div class="mt-2 text-xs opacity-60">
        <span v-if="copied">✅ Copié !</span>
      </div>
    </div>
  </div>
</template>